import React, { Suspense, lazy, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { pick } from 'lodash';

import { Configurations, WidgetName, WidgetsObject } from './models';
import type { WidgetErrorType } from './utils/tracker';

import { AppContext } from './context';

import { useTracker } from './context/constructorContext';

const Searcher = lazy(() => import('./pages/searcher/Searcher'));
const Direction = lazy(() => import('./pages/directionWidget/DirectionWidget'));
const Search = lazy(() => import('./pages/SearchWidget/SearchWidget'));
const Package = lazy(() => import('./pages/package/Package'));
const Hotel = lazy(() => import('./pages/hotelWidget/HotelWidget'));
const CigaretteWidget = lazy(() => import('./pages/Cigarette/CigaretteWidget'));

const WIDGETS: WidgetsObject = {
  SEARCHER: Searcher,
  DIRECTION: Direction,
  METAREGIONS: Direction,
  SEARCH: Search,
  PACKAGE: Package,
  HOTEL: Hotel,
  CIGARETTE: CigaretteWidget,
};

export const App = (props: Configurations): JSX.Element => {
  const Widget = useMemo(() => {
    if (!props?.widgetName) {
      throw new Error('Не указано название виджета');
    }
    const activeWidget = props?.widgetName?.toUpperCase();

    if (!(activeWidget in WidgetName)) {
      throw new Error('Неверное название виджета');
    }

    return WIDGETS[activeWidget];
  }, [props?.widgetName]);

  const widgetConfig = {
    ...pick(props, ['widgetName', 'partnerKey', 'searchParams']),
    ...props.widgetParams,
  };

  const trackerMethods = useTracker(props.widgetName, props.widgetParams?.whitelabelName);

  const handleError = (error: Error) => {
    if (!trackerMethods) {
      return;
    }

    const { trackError } = trackerMethods;

    const isValidErrorType = ['error_render', 'error_data'].includes(error.message);
    const errorType = (isValidErrorType ? error.message : 'error_render') as WidgetErrorType;

    console.log('Error', error.message, error.stack);
    trackError(errorType);
  };

  return (
    <ErrorBoundary FallbackComponent={() => <></>} onError={handleError}>
      <AppContext
        initialState={props.widgetData}
        config={{
          ...widgetConfig,
        }}
        isOnPage
      >
        <Suspense fallback={<div>loading...</div>}>
          {Widget && <Widget initialState={props.widgetData} />}
        </Suspense>
      </AppContext>
    </ErrorBoundary>
  );
};
